import type { Provider } from '@/types';

/**
 * Набор цветов для провайдеров
 */
export const PROVIDER_COLORS = ['flamingo', 'blue', 'red', 'success', 'green', 'accent'];

/**
 * Яркие цвета для использования в картах игр и блоках
 */
export const VIBRANT_COLORS = [
    'rgb(252, 81, 133)', // red
    'rgb(80, 37, 209)', // blue
    'rgb(142, 36, 170)', // accent/primary
    'rgb(76, 175, 80)', // success
    'rgb(103, 71, 199)', // tertiary/secondary
    'rgb(112, 116, 191)', // blue-light
    'rgb(171, 54, 206)', // primary-hover/light/active
    'rgb(0, 189, 113)' // button-success
];

/**
 * Получает случайный цвет из списка провайдеров
 */
export const getRandomProviderColor = (): string => {
    return PROVIDER_COLORS[Math.floor(Math.random() * PROVIDER_COLORS.length)];
};

/**
 * Получает случайный яркий цвет из списка
 */
export const getRandomVibrantColor = (): string => {
    const randomIndex = Math.floor(Math.random() * VIBRANT_COLORS.length);
    return VIBRANT_COLORS[randomIndex];
};

/**
 * Возвращает цвет из списка по индексу с зацикливанием
 * @param colors Список цветов
 * @param index Индекс элемента
 */
export const getColorByIndex = (colors: string[], index: number): string => {
    return colors[index % colors.length];
};

/**
 * Преобразует строку RGB в объект с компонентами r, g, b
 */
export const parseRgb = (rgbString: string): { r: number; g: number; b: number } => {
    const matches = rgbString.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (!matches) {
        return { r: 0, g: 0, b: 0 };
    }
    return {
        r: parseInt(matches[1], 10),
        g: parseInt(matches[2], 10),
        b: parseInt(matches[3], 10)
    };
};

/**
 * Генерирует строку градиента на основе базового цвета
 */
export const generateGradient = (baseColor: string): string => {
    const rgb = parseRgb(baseColor);

    // rgba для основного цвета с разной прозрачностью
    const rgba100 = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`;
    const rgba82 = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.82)`;

    // Получаем второй случайный цвет для градиента
    const secondColor = getRandomVibrantColor();
    const secondRgb = parseRgb(secondColor);
    const rgba0 = `rgba(${secondRgb.r}, ${secondRgb.g}, ${secondRgb.b}, 0)`;

    return `${rgba100} 100%, ${rgba82} 82%, ${rgba0} 0%`;
};

/**
 * Присваивает случайный цвет каждому элементу из списка
 * @param items Массив элементов
 * @param colors Массив цветов для выбора
 * @returns Массив элементов с добавленным свойством color
 */
export function assignRandomColors<T>(items: T[], colors: string[] = VIBRANT_COLORS): (T & { color: string })[] {
    return items.map((item) => {
        // Выбираем случайный цвет из списка
        const randomIndex = Math.floor(Math.random() * colors.length);
        const color = colors[randomIndex];

        // Возвращаем элемент с добавленным цветом
        return {
            ...item,
            color
        };
    });
}

/**
 * Присваивает случайный цвет каждому провайдеру из списка
 * @param providers Массив провайдеров
 * @returns Массив провайдеров с добавленным свойством color
 */
export function assignColorsToProviders<T extends Provider>(providers: T[]): (T & { color: string })[] {
    return assignRandomColors(providers, PROVIDER_COLORS);
}

/**
 * Возвращает новый массив элементов, где цвета распределены равномерно
 * (чтобы избежать ситуации, когда много соседних элементов имеют одинаковый цвет)
 * @param items Массив элементов
 * @param colors Массив цветов для распределения
 * @returns Массив элементов с добавленным свойством color
 */
export function assignBalancedColors<T>(items: T[], colors: string[] = VIBRANT_COLORS): (T & { color: string })[] {
    return items.map((item, index) => {
        // Равномерно распределяем цвета по списку
        const colorIndex = index % colors.length;
        const color = colors[colorIndex];

        return {
            ...item,
            color
        };
    });
}

/**
 * Возвращает новый массив провайдеров, где цвета распределены равномерно
 * @param providers Массив провайдеров
 * @returns Массив провайдеров с добавленным свойством color
 */
export function assignBalancedColorsToProviders<T extends Provider>(providers: T[]): (T & { color: string })[] {
    return assignBalancedColors(providers, PROVIDER_COLORS);
}
